import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Spin, Result, Button, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  HomeOutlined,
  CreditCardOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { fetchSubInfo, logout } from "../../../store/slices/auth.slice";
import subService from "../../../services/sub/sub.service";
import { all_routes } from "../../../router/all_routes";

const { Title, Text } = Typography;

interface PaymentVerificationResult {
  message: string;
  subscription: {
    status: string;
    type: string;
    referenceCode: string;
    expiryDate: string;
  };
}

const SubscriptionCallback: React.FC = () => {
  const navigate = useNavigate();
  // const [searchParams] = useSearchParams();
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState<PaymentVerificationResult | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Döngü koruması için useRef
  const hasVerified = useRef(false);
  const componentId = useRef(Math.random().toString(36).substr(2, 9));

  useEffect(() => {
    console.log("🔄 [COMPONENT LIFECYCLE] useEffect tetiklendi:", {
      componentId: componentId.current,
      timestamp: new Date().toISOString(),
      hasVerified: hasVerified.current,
      loading,
      currentPath: window.location.pathname,
      currentSearch: window.location.search,
      localStorageKeys: Object.keys(localStorage),
      sessionStorageKeys: Object.keys(sessionStorage)
    });

    // Önceki başarılı işlem kontrolü
    const previousSuccess = sessionStorage.getItem('subscription_callback_success');
    if (previousSuccess) {
      console.log("✅ [PREVIOUS SUCCESS] Önceki başarılı işlem bulundu:", {
        componentId: componentId.current,
        timestamp: new Date().toISOString(),
        previousSuccess: JSON.parse(previousSuccess)
      });
      
      const successData = JSON.parse(previousSuccess);
      setResult(successData);
      setLoading(false);
      return;
    }

    // Döngü koruması
    if (hasVerified.current) {
      console.log("⚠️ [DUPLICATE PREVENTION] Zaten doğrulama yapıldı, atlanıyor:", {
        componentId: componentId.current,
        timestamp: new Date().toISOString(),
        hasVerified: hasVerified.current
      });
      return;
    }
    hasVerified.current = true;

    const verifyPayment = async () => {
      console.log("🚀 [VERIFICATION START] Ödeme doğrulama başlatıldı:", {
        componentId: componentId.current,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        pathname: window.location.pathname,
        search: window.location.search
      });
      
      try {
        const companyId = localStorage.getItem("companyId");
        const token = localStorage.getItem("checkoutsession");
        const conversationId = localStorage.getItem("conversationId");

        console.log("📊 [TOKEN ANALYSIS] Token analizi:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          hasCompanyId: !!companyId,
          hasToken: !!token,
          hasConversationId: !!conversationId,
          companyIdPreview: companyId ? companyId.substring(0, 10) + "..." : "YOK",
          tokenPreview: token ? token.substring(0, 20) + "..." : "YOK",
          conversationIdPreview: conversationId ? conversationId.substring(0, 20) + "..." : "YOK",
          allLocalStorageKeys: Object.keys(localStorage),
          localStorageSize: Object.keys(localStorage).length
        });

        if (!token) {
          console.log("❌ [TOKEN NOT FOUND] Token bulunamadı:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            localStorageKeys: Object.keys(localStorage),
            hasCheckoutsession: localStorage.getItem("checkoutsession") !== null,
            checkoutsessionValue: localStorage.getItem("checkoutsession"),
            reason: "Token localStorage'da yok"
          });

          setResult({
            message:
              "Ödeme bulunamadı. Lütfen ödeme işlemini tekrar başlatın.",
            subscription: {
              status: "error",
              type: "monthly",
              referenceCode: "",
              expiryDate: "",
            },
          });
          setLoading(false);
          return;
        }

        // ConversationId eksik kontrol
        if (!conversationId) {
          console.log("❌ [CONVERSATION ID NOT FOUND] ConversationId bulunamadı:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            hasToken: !!token,
            tokenPreview: token.substring(0, 20) + "...",
            localStorageKeys: Object.keys(localStorage),
            hasConversationId: localStorage.getItem("conversationId") !== null,
            conversationIdValue: localStorage.getItem("conversationId"),
            reason: "ConversationId localStorage'da yok"
          });

          setResult({
            message:
              "Ödeme bulunamadı. Lütfen ödeme işlemini tekrar başlatın.",
            subscription: {
              status: "error",
              type: "monthly",
              referenceCode: "",
              expiryDate: "",
            },
          });
          setLoading(false);
          return;
        }

        console.log("🧹 [TOKEN CLEANUP BEFORE API] API çağrısı öncesi token temizleme:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          tokenPreview: token.substring(0, 20) + "...",
          conversationIdPreview: conversationId.substring(0, 20) + "...",
          reason: "Döngü önleme - API çağrısı öncesi temizlik"
        });

        localStorage.removeItem('checkoutsession');
        localStorage.removeItem('conversationId');

        // Backend'e doğrulama isteği gönder
        console.log("🔄 [API CALL START] Backend'e doğrulama isteği gönderiliyor:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          tokenPreview: token.substring(0, 20) + "...",
          conversationIdPreview: conversationId.substring(0, 20) + "...",
          companyIdPreview: companyId ? companyId.substring(0, 10) + "..." : "YOK",
          apiEndpoint: "subService.verifyPayment"
        });

        const verificationResult = await subService.verifyPayment(
          token,
          conversationId,
          companyId
        );

        console.log("✅ [API RESPONSE] Backend'den cevap alındı:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          hasResult: !!verificationResult,
          hasSubscription: !!verificationResult?.subscription,
          subscriptionStatus: verificationResult?.subscription?.status,
          subscriptionType: verificationResult?.subscription?.type,
          message: verificationResult?.message,
          fullResponse: verificationResult
        });



        // 🔒 BAŞARILI DOĞRULAMA: Token'ları temizle
        if (verificationResult.subscription?.status === "active") {
          console.log("🎉 [SUCCESS CLEANUP] Başarılı doğrulama - token temizleme:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            subscriptionStatus: verificationResult.subscription.status,
            reason: "Başarılı abonelik doğrulaması"
          });
          localStorage.removeItem("checkoutsession");
          localStorage.removeItem("conversationId");
        } else {
          console.log("⚠️ [NON-ACTIVE STATUS] Aktif olmayan durum:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            subscriptionStatus: verificationResult.subscription?.status,
            reason: "Abonelik aktif değil, token temizlenmedi"
          });
        }

        // Response'u state'e kaydet
        console.log("📝 [STATE UPDATE] Result state güncelleniyor:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          message: "Ödeme işlemi tamamlandı",
          subscription: verificationResult.subscription
        });

        const resultData = {
          message: "Ödeme işlemi tamamlandı",
          subscription: verificationResult.subscription,
        };

        setResult(resultData);

        // Başarılı işlemi sessionStorage'a kaydet (component remount durumunda kullanmak için)
        if (verificationResult.subscription?.status === "active") {
          console.log("💾 [SESSION STORAGE] Başarılı işlem sessionStorage'a kaydediliyor:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            data: resultData
          });
          sessionStorage.setItem('subscription_callback_success', JSON.stringify(resultData));
        }

        // Eğer abonelik aktif ise
        if (verificationResult.subscription.status === "active") {
          console.log("🔄 [ACTIVE SUBSCRIPTION] Aktif abonelik işlemleri:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            subscriptionStatus: verificationResult.subscription.status
          });

          // Mevcut kullanıcı için sub bilgilerini yenile
          const user = JSON.parse(localStorage.getItem("user") || "{}");
          console.log("👤 [USER INFO] Kullanıcı bilgisi:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            hasUser: !!user,
            hasCompanyId: !!user.companyId,
            companyIdPreview: user.companyId ? user.companyId.toString().substring(0, 10) + "..." : "YOK"
          });

          if (user.companyId) {
            console.log("🔄 [DISPATCH FETCH SUB] Sub bilgileri yenileniyor:", {
              componentId: componentId.current,
              timestamp: new Date().toISOString(),
              companyId: user.companyId
            });
            dispatch(fetchSubInfo(user.companyId));
          }

          // // 3 saniye countdown başlat
          // setIsRedirecting(true);
          // setCountdown(3);

          // const countdownInterval = setInterval(() => {
          //   setCountdown((prev) => {
          //     if (prev <= 1) {
          //       clearInterval(countdownInterval);
          //       // Logout yap ve login sayfasına yönlendir
          //       handleLogoutAndRedirect();
          //       return 0;
          //     }
          //     return prev - 1;
          //   });
          // }, 1000);
        } else {
          console.log("❌ [INACTIVE SUBSCRIPTION] Aktif olmayan abonelik:", {
            componentId: componentId.current,
            timestamp: new Date().toISOString(),
            subscriptionStatus: verificationResult.subscription.status,
            subscriptionType: verificationResult.subscription.type
          });
          // setResult({
          //   message: "Ödeme işlemi başarısız oldu.",
          //   subscription: {
          //     status: "Hata",
          //     type: "Kullanıcı Ekleme",
          //     referenceCode: "",
          //     expiryDate: "",
          //   },
          // });
        }
      } catch (error: any) {
        console.error("💥 [API ERROR] Backend API hatası:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          error: error,
          errorMessage: error?.message,
          errorResponse: error?.response,
          errorResponseData: error?.response?.data,
          errorResponseStatus: error?.response?.status,
          errorStack: error?.stack,
          fullError: JSON.stringify(error, null, 2)
        });

        // Hata durumunda da token'ları temizle (döngü önleme)
        console.log("🧹 [ERROR CLEANUP] Hata durumunda token temizleme:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          reason: "API hatası - döngü önleme"
        });
        localStorage.removeItem("checkoutsession");
        localStorage.removeItem("conversationId");

        const errorMessage =
          // error.response?.data?.message ||
          // error.response?.data?.error ||
          // error.message ||
          "Ödeme doğrulama sırasında bir hata oluştu.";

        console.log("📝 [ERROR STATE] Hata state'i güncelleniyor:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          errorMessage: errorMessage
        });

        setResult({
          message: errorMessage,
          subscription: {
            status: "error",
            type: "monthly",
            referenceCode: "",
            expiryDate: "",
          },
        });
      } finally {
        console.log("🏁 [FINALLY BLOCK] İşlem tamamlandı:", {
          componentId: componentId.current,
          timestamp: new Date().toISOString(),
          setLoadingToFalse: true
        });
        setLoading(false);
      }
    };

    verifyPayment();
  }, []); 

  const handleGoToDashboard = () => {
    console.log("🏠 [NAVIGATE TO DASHBOARD] Ana sayfaya yönlendirme:", {
      componentId: componentId.current,
      timestamp: new Date().toISOString(),
      targetRoute: all_routes.adminDashboard
    });
    
    // Başarılı işlem kaydını temizle
    sessionStorage.removeItem('subscription_callback_success');
    navigate(all_routes.adminDashboard);
  };

  const handleGoToSettings = () => {
    console.log("⚙️ [NAVIGATE TO SETTINGS] Ayarlara yönlendirme:", {
      componentId: componentId.current,
      timestamp: new Date().toISOString(),
      targetRoute: all_routes.companySettings + "?tab=payment-settings"
    });
    
    // Başarılı işlem kaydını temizle
    sessionStorage.removeItem('subscription_callback_success');
    navigate(all_routes.companySettings + "?tab=payment-settings");
  };

  const handleRetryPayment = () => {
    console.log("🔄 [RETRY PAYMENT] Ödeme tekrar deneme:", {
      componentId: componentId.current,
      timestamp: new Date().toISOString(),
      targetRoute: all_routes.companySettings + "?tab=payment-settings"
    });
    navigate(all_routes.companySettings + "?tab=payment-settings");
  };

  const handleLogoutAndRedirect = () => {
    console.log("🚪 [LOGOUT AND REDIRECT] Logout ve yönlendirme:", {
      componentId: componentId.current,
      timestamp: new Date().toISOString(),
      targetRoute: all_routes.login
    });
    dispatch(logout());
    navigate(all_routes.login);
  };

  // Component unmount logları
  useEffect(() => {
    return () => {
      console.log("💀 [COMPONENT UNMOUNT] Component unmount ediliyor:", {
        componentId: componentId.current,
        timestamp: new Date().toISOString(),
        hasVerified: hasVerified.current,
        currentLoading: loading,
        currentResult: !!result
      });
    };
  }, [loading, result]);

  // Render logları
  console.log("🎨 [RENDER] Component render ediliyor:", {
    componentId: componentId.current,
    timestamp: new Date().toISOString(),
    loading,
    hasResult: !!result,
    resultSubscriptionStatus: result?.subscription?.status,
    isRedirecting,
    countdown
  });

  if (loading) {
    console.log("⏳ [LOADING RENDER] Loading ekranı render ediliyor:", {
      componentId: componentId.current,
      timestamp: new Date().toISOString()
    });
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <Card style={{ maxWidth: 500, width: "100%", textAlign: "center" }}>
              <div style={{ padding: "40px 20px" }}>
                <Spin
                  indicator={
                    <LoadingOutlined
                      style={{ fontSize: 48, color: "#1890ff" }}
                      spin
                    />
                  }
                  size="large"
                />
                <div style={{ marginTop: 24 }}>
                  <Title level={4}>Ödeme Doğrulanıyor...</Title>
                  <Text type="secondary">
                    Abonelik ödemeniz doğrulanıyor, lütfen bekleyin...
                  </Text>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  console.log("✅ [RESULT RENDER] Sonuç ekranı render ediliyor:", {
    componentId: componentId.current,
    timestamp: new Date().toISOString(),
    subscriptionStatus: result?.subscription?.status,
    isActive: result?.subscription.status === "active"
  });

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "60vh" }}
        >
          <Card style={{ maxWidth: 600, width: "100%" }}>
            {result?.subscription.status === "active" ? (
              <Result
                icon={
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", fontSize: 72 }}
                  />
                }
                title={
                  <Title
                    level={3}
                    style={{ color: "#52c41a", marginBottom: 16 }}
                  >
                    🎉 Aboneliğiniz Başarıyla Güncellendi!
                  </Title>
                }
                subTitle={
                  <div>
                    <Text
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      {result.message}
                    </Text>
                    <Text
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 12,
                        color: "#52c41a",
                        fontWeight: 600,
                      }}
                    >
                      Abonelik değişikliğiniz başarıyla işlendi!
                    </Text>
                    <Text
                      style={{
                        fontSize: 14,
                        display: "block",
                        marginBottom: 8,
                        color: "#1890ff",
                      }}
                    >
                      Yeni abonelik ayarlarınız aktif edildi.
                    </Text>
                    {isRedirecting && (
                      <div
                        style={{
                          marginTop: 20,
                          padding: 16,
                          background: "#f0f9ff",
                          borderRadius: 8,
                          border: "1px solid #1890ff",
                        }}
                      >
                      </div>
                    )}
                  </div>
                }
                extra={
                  !isRedirecting
                    ? [
                        <Button
                          type="primary"
                          key="dashboard"
                          icon={<HomeOutlined />}
                          onClick={handleGoToDashboard}
                          size="large"
                        >
                          Ana Sayfaya Git
                        </Button>,
                        <Button
                          key="settings"
                          icon={<SettingOutlined />}
                          onClick={handleGoToSettings}
                        >
                          Abonelik Ayarları
                        </Button>,
                      ]
                    : [
                        <Button
                          key="manual-redirect"
                          type="primary"
                          icon={<HomeOutlined />}
                          onClick={handleLogoutAndRedirect}
                          size="large"
                        >
                          Hemen Giriş Sayfasına Git
                        </Button>,
                      ]
                }
              />
            ) : (
              <Result
                icon={
                  <CloseCircleOutlined
                    style={{ color: "#ff4d4f", fontSize: 72 }}
                  />
                }
                title={
                  <Title
                    level={3}
                    style={{ color: "#ff4d4f", marginBottom: 16 }}
                  >
                    {result?.subscription.status &&
                    result.subscription.status !== "active"
                      ? "Abonelik Güncellenemedi"
                      : "Ödeme Başarısız"}
                  </Title>
                }
                subTitle={
                  <div>
                    <Text
                      style={{
                        fontSize: 16,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      {"Ödeme işlemi tamamlanamadı."}
                    </Text>
                    {/* <Text
                      type="secondary"
                      style={{
                        fontSize: 14,
                        display: "block",
                        marginBottom: 8,
                      }}
                    >
                      {result?.subscription.status &&
                      result.subscription.status !== "active"
                        ? `Abonelik durumu: ${result.subscription.status}`
                        : "Lütfen kart bilgilerinizi kontrol edip tekrar deneyin."}
                    </Text> */}
                  </div>
                }
                extra={[
                  <Button
                    type="primary"
                    key="retry"
                    icon={<CreditCardOutlined />}
                    onClick={handleRetryPayment}
                    size="large"
                  >
                    Tekrar Dene
                  </Button>,
                  <Button
                    key="dashboard"
                    icon={<HomeOutlined />}
                    onClick={handleGoToDashboard}
                  >
                    Ana Sayfaya Git
                  </Button>,
                ]}
              />
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCallback;
